import { formatDatetime } from "@/utils/Formatters";
import axios from "axios";
import dayjs from "dayjs";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export enum EEnvironmentType {
  DEV = "DEV",
  FEATURE = "FEATURE",
  MR = "MR",
  DEVEL = "DEVEL",
  QA = "QA",
  DEMO = "DEMO",
  PRODUCTION = "PRODUCTION",
}

const EnvironmentsMap = {
  [EEnvironmentType.DEV]: {
    color: "liteDef5",
    gradient: "tw:!bg-gradient-to-r tw:from-sky-500 tw:to-blue-600",
  },
  [EEnvironmentType.FEATURE]: {
    color: "purple",
  },
  [EEnvironmentType.MR]: {
    color: "primary",
  },
  [EEnvironmentType.DEVEL]: {
    color: "phase05",
  },
  [EEnvironmentType.QA]: {
    color: "phase05",
  },
  [EEnvironmentType.DEMO]: {
    color: "phase05",
  },
  [EEnvironmentType.PRODUCTION]: {},
};

interface TEnvironmentBadge {
  label: string;
  color?: string;
  gradient?: string;
}

export const useEnvironmentStore = defineStore("environment", () => {
  const currentEnv = import.meta.env.VITE_ENVIRONMENT || EEnvironmentType.DEV;
  const currentEnvMeta = EnvironmentsMap[currentEnv as keyof typeof EnvironmentsMap];
  const version = import.meta.env.VITE_VERSION || "unknown";
  const buildDate = ref(import.meta.env.VITE_BUILD_DATE || "unknown");

  const badge = computed((): TEnvironmentBadge | null => {
    if (!currentEnvMeta || !("color" in currentEnvMeta)) {
      return null;
    }

    let label = currentEnv;

    if (currentEnv === EEnvironmentType.MR && version.startsWith("mr:")) {
      const mrNumber = version.split(":")[1];
      label = `MR ${mrNumber}`;
    }

    return {
      label,
      ...currentEnvMeta,
    };
  });

  const checkForUpdates = async () => {
    try {
      const response = await axios.get("/version.json", {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10_000,
      });

      if (response.data && response.data.buildTime) {
        buildDate.value = formatDatetime(dayjs(response.data.buildTime));
      }

      if (response.data && response.data.version) {
        return response.data.version !== version;
      }
    } catch (error) {
      logError(error, {
        context: { feature: "environment", step: "checkForUpdates" },
      });
      console.error("Failed to check for updates:", error);
    }

    return false;
  };

  return {
    currentEnv,
    version,
    badge,
    buildDate,
    checkForUpdates,
  };
});
